import {
	ActionListDivider,
	ActionListHeader,
	ActionListItem,
	DropOverActionList
} from "client/Components";
import { InlineOptionSelectorPreview } from "client/Components/InlineOptionSelector/InlineOptionSelectorPreview";
import { generateTrackingId, setTrackingId } from "client/Misc/utils";
import { EmailSortDirection, EmailSortOrder } from "client/Api/Email/email";
import {
	ReactElement,
	RefObject,
	useCallback,
	useRef,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import styles from "client/Components/InlineOptionSelector/InlineOptionSelector.module.less";

export type SortOptionSelectorProps = {
	field: Exclude<EmailSortOrder, EmailSortOrder.None>;
	direction: EmailSortDirection;
	onFieldChange(field: Exclude<EmailSortOrder, EmailSortOrder.None>): void;
	onDirectionChange(direction: EmailSortDirection): void;
};

export const SortOptionSelector = (props: SortOptionSelectorProps): ReactElement => {
	const {
		field,
		direction,
		onFieldChange,
		onDirectionChange
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const ref: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const previewText: string = localize(emailSortOrderToNameKey[field]);

	const renderSortOrderCheckbox = useCallback(
		(checkboxField: Exclude<EmailSortOrder, EmailSortOrder.None>): ReactElement => {
			const changeHandler = (): void => {
				onFieldChange(checkboxField);
			};

			const isSelected: boolean = checkboxField === field;

			return (
				<ActionListItem
					isActive={isSelected}
					rightIcon={isSelected ? "check" : undefined}
					onClick={changeHandler}
					text={localize(emailSortOrderToNameKey[checkboxField])}
					trackingId={generateTrackingId(`sort-order|${checkboxField}`)}
				/>
			);
		},
		[field, onFieldChange]
	);

	const renderSortDirectionCheckbox = useCallback(
		(checkboxDirection: Exclude<EmailSortDirection, EmailSortDirection.Default>): ReactElement => {
			return (
				<SortDirectionCheckbox
					direction={direction}
					onDirectionChange={onDirectionChange}
					checkboxDirection={checkboxDirection}
					field={field}
				/>
			);
		},
		[direction, field, onDirectionChange]
	);

	return (
		<div {...setTrackingId("sort-by-section")}>
			{localize("_SortBy")}
			<span
				className={styles.root}
			>
				<InlineOptionSelectorPreview
					ref={ref}
					onClick={() => setIsOpen(isOpen => !isOpen)}
					trackingId="email-sort-option-selector"
					isLeftAligned
				>
					{ previewText }
				</InlineOptionSelectorPreview>
				{isOpen && ref.current && (
					<DropOverActionList
						target={ref.current}
						onClose={() => setIsOpen(false)}
						trackingId="email-sort-option-selector__dropover"
						targetAlignment="bl"
						contentAlignment="tl"
						menuTitle={localize("_SortBy")}
					>
						<ActionListHeader text={localize("_SortOn")} />
						{renderSortOrderCheckbox(EmailSortOrder.Date)}
						{renderSortOrderCheckbox(EmailSortOrder.From)}
						{renderSortOrderCheckbox(EmailSortOrder.Subject)}
						<ActionListDivider />
						<ActionListHeader text={localize("_Order")} />
						{renderSortDirectionCheckbox(EmailSortDirection.Ascending)}
						{renderSortDirectionCheckbox(EmailSortDirection.Descending)}
					</DropOverActionList>
				)}
			</span>
		</div>
	);
};
SortOptionSelector.displayName="SortOptionSelector";

const emailSortOrderToNameKey: {[key in Exclude<EmailSortOrder, EmailSortOrder.None>]: LocalizationKey} = {
	[EmailSortOrder.Date]: "_Date",
	[EmailSortOrder.From]: "_From",
	[EmailSortOrder.Subject]: "_Subject"
};

type SortDirectionCheckboxProps = {
	direction: EmailSortDirection;
	onDirectionChange(direction: EmailSortDirection): void;
	checkboxDirection: EmailSortDirection;
	field: EmailSortOrder;
};

const SortDirectionCheckbox = (props: SortDirectionCheckboxProps): ReactElement => {
	const {
		direction,
		checkboxDirection,
		field,
		onDirectionChange
	} = props;

	let isDefault: boolean;
	let ascendingDirectionText: string;
	let descendingDirectionText: string;
	switch (field) {
		case EmailSortOrder.Date:
		case EmailSortOrder.None:
			isDefault = checkboxDirection === EmailSortDirection.Descending;
			ascendingDirectionText = localize("_OldestOnTop");
			descendingDirectionText = localize("_NewestOnTop");
			break;
		case EmailSortOrder.From:
		case EmailSortOrder.Subject:
			isDefault = checkboxDirection === EmailSortDirection.Ascending;
			ascendingDirectionText = localize("_AOnTop");
			descendingDirectionText = localize("_ZOnTop");
			break;
		default:
			never(field);
	}

	const changeHandler = (): void => {
		onDirectionChange(checkboxDirection);
	};

	const isSelected: boolean = checkboxDirection === direction || (isDefault && direction === EmailSortDirection.Default);

	return (
		<ActionListItem
			isActive={isSelected}
			rightIcon={isSelected ? "check" : undefined}
			onClick={changeHandler}
			text={checkboxDirection === EmailSortDirection.Ascending ? ascendingDirectionText : descendingDirectionText}
			trackingId={`sort-direction|${checkboxDirection}`}
		/>
	);
};
SortDirectionCheckbox.displayName = "SortDirectionCheckbox";
